import { useEffect, useState } from 'react';
import { vehicleAPI, renewalAPI, driverAPI, partAPI } from '../services/api';
import { formatRs } from '../utils/currency';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Modal, Input, Select, Loading, Alert } from '../components/ui';

// ── Constants ─────────────────────────────────────────────────────────────────
const DOC_TYPES = [
  { key: 'insurance',       label: 'Insurance',           icon: '🛡', color: 'blue',   no_field: 'insurance_no',       expiry_field: 'insurance_expiry' },
  { key: 'revenue_licence', label: 'Revenue Licence',     icon: '📋', color: 'green',  no_field: 'revenue_licence_no', expiry_field: 'revenue_licence_expiry' },
  { key: 'emission_test',   label: 'Emission Test',       icon: '🌿', color: 'amber',  no_field: 'emission_test_no',   expiry_field: 'emission_test_expiry' },
  { key: 'fitness_cert',    label: 'Fitness Certificate', icon: '✅', color: 'purple', no_field: 'fitness_cert_no',    expiry_field: 'fitness_cert_expiry' },
];

const COLOR = {
  blue:   { hdr:'bg-blue-600',   light:'bg-blue-50',   border:'border-blue-200',   text:'text-blue-700',   badge:'bg-blue-100 text-blue-700' },
  green:  { hdr:'bg-green-600',  light:'bg-green-50',  border:'border-green-200',  text:'text-green-700',  badge:'bg-green-100 text-green-700' },
  amber:  { hdr:'bg-amber-500',  light:'bg-amber-50',  border:'border-amber-200',  text:'text-amber-700',  badge:'bg-amber-100 text-amber-700' },
  purple: { hdr:'bg-purple-600', light:'bg-purple-50', border:'border-purple-200', text:'text-purple-700', badge:'bg-purple-100 text-purple-700' },
};

const daysLeft = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

const statusInfo = (expiry) => {
  const d = daysLeft(expiry);
  if (d === null) return { label: 'Not Set',  cls: 'bg-gray-100 text-gray-500',    dot: 'bg-gray-400' };
  if (d < 0)      return { label: 'Expired',  cls: 'bg-red-100 text-red-700',      dot: 'bg-red-500',    days: Math.abs(d) + 'd overdue' };
  if (d <= 30)    return { label: 'Critical', cls: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', days: d + ' days left' };
  if (d <= 60)    return { label: 'Warning',  cls: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-400',  days: d + ' days left' };
  return           { label: 'Valid',    cls: 'bg-green-100 text-green-700',   dot: 'bg-green-500',  days: d + ' days left' };
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function Renewals() {
  const { can } = useAuth();
  const [vehicles,    setVehicles]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [activeDoc,   setActiveDoc]   = useState('insurance');   // which doc type tab
  const [filterStat,  setFilterStat]  = useState('all');         // all | expired | critical | warning | valid
  const [search,      setSearch]      = useState('');

  // Renew modal
  const [renewModal,  setRenewModal]  = useState(false);
  const [renewVehicle,setRenewVehicle]= useState(null);
  const [renewDoc,    setRenewDoc]    = useState(null);
  const [renewForm,   setRenewForm]   = useState({ doc_no: '', expiry: '' });
  const [saving,      setSaving]      = useState(false);
  const [view,        setView]        = useState('current');   // current | history
  const [history,     setHistory]     = useState([]);
  const [histVehicle, setHistVehicle] = useState('');
  const [histType,    setHistType]    = useState('');
  const [leases,      setLeases]      = useState([]);
  const [licences,    setLicences]    = useState([]);
  const [warranties,  setWarranties]  = useState([]);

  // Stats per doc type
  const [stats, setStats] = useState({});

  const flash = (msg, isErr = false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const load = () => {
    setLoading(true);
    vehicleAPI.getAll({ limit: 200 })
      .then(r => {
        setVehicles(r.data.data);
        // Compute stats
        const s = {};
        DOC_TYPES.forEach(dt => {
          const items = r.data.data.map(v => daysLeft(v[dt.expiry_field]));
          s[dt.key] = {
            expired:  items.filter(d => d !== null && d < 0).length,
            critical: items.filter(d => d !== null && d >= 0 && d <= 30).length,
            warning:  items.filter(d => d !== null && d > 30 && d <= 60).length,
            valid:    items.filter(d => d !== null && d > 60).length,
            not_set:  items.filter(d => d === null).length,
          };
        });
        setStats(s);
      })
      .catch(() => setError('Failed to load vehicles'))
      .finally(() => setLoading(false));
  };

  const loadHistory = () => {
    renewalAPI.getAll({
      ...(histVehicle && { vehicle_id: histVehicle }),
      ...(histType && { doc_type: histType }),
    })
      .then(r => setHistory(r.data.data || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadHistory(); }, [histVehicle, histType]);
  useEffect(() => {
    vehicleAPI.leaseAlerts().then(r => setLeases(r.data.data || [])).catch(() => setLeases([]));
    driverAPI.licenseAlerts().then(r => setLicences(r.data.data || [])).catch(() => setLicences([]));
    partAPI.warrantyAlerts().then(r => setWarranties(r.data.data || [])).catch(() => setWarranties([]));
  }, []);

  const licSoon = licences.filter(l => l.days_left >= 0 && l.days_left <= 60).length;
  const licOver = licences.filter(l => l.days_left < 0).length;
  const wSoon = warranties.filter(w => w.days_left >= 0 && w.days_left <= 60).length;
  const wOver = warranties.filter(w => w.days_left < 0).length;

  const leaseSoon = leases.filter(l => l.months_left !== null && l.months_left <= 3 && l.days_left >= 0).length;
  const leaseOver = leases.filter(l => l.days_left < 0).length;

  // Current doc type config
  const doc = DOC_TYPES.find(d => d.key === activeDoc);
  const col = COLOR[doc.color];

  // Filtered vehicles for current doc type
  const filtered = vehicles.filter(v => {
    const d = daysLeft(v[doc.expiry_field]);
    const si = statusInfo(v[doc.expiry_field]);
    const matchSearch = !search || v.reg_number.toLowerCase().includes(search.toLowerCase())
      || `${v.brand} ${v.model}`.toLowerCase().includes(search.toLowerCase());
    const matchStat = filterStat === 'all' ||
      (filterStat === 'expired'  && si.label === 'Expired')  ||
      (filterStat === 'critical' && si.label === 'Critical') ||
      (filterStat === 'warning'  && si.label === 'Warning')  ||
      (filterStat === 'valid'    && si.label === 'Valid')     ||
      (filterStat === 'not_set'  && si.label === 'Not Set');
    return matchSearch && matchStat;
  }).sort((a, b) => {
    const da = daysLeft(a[doc.expiry_field]) ?? 9999;
    const db = daysLeft(b[doc.expiry_field]) ?? 9999;
    return da - db;
  });

  // Open renew modal
  const openRenew = (vehicle, docType) => {
    setRenewVehicle(vehicle);
    setRenewDoc(docType);
    setRenewForm({
      doc_no: vehicle[docType.no_field] || '',
      provider: docType.key === 'insurance' ? (vehicle.insurance_provider || '') : '',
      from_date: new Date().toISOString().split('T')[0],
      expiry: '',
      sum_insured: '', premium_quoted: '', approved_premium: '',
      amount: '', payment_method: 'cheque', cheque_no: '', payment_date: '',
      notes: '',
    });
    setRenewModal(true);
  };

  // Save renewal
  const saveRenewal = async e => {
    e.preventDefault();
    if (!renewForm.expiry) return flash('Expiry date is required', true);
    setSaving(true);
    try {
      await renewalAPI.create({
        vehicle_id: renewVehicle.id,
        doc_type:   renewDoc.key,
        doc_no:     renewForm.doc_no || null,
        provider:   renewForm.provider || null,
        from_date:  renewForm.from_date || null,
        to_date:    renewForm.expiry,
        sum_insured:      renewForm.sum_insured || null,
        premium_quoted:   renewForm.premium_quoted || null,
        approved_premium: renewForm.approved_premium || null,
        amount:         renewForm.amount || null,
        payment_method: renewForm.payment_method || null,
        cheque_no:      renewForm.cheque_no || null,
        payment_date:   renewForm.payment_date || null,
        notes:          renewForm.notes || null,
      });
      setRenewModal(false);
      load();
      loadHistory();
      flash(`${renewDoc.label} renewed for ${renewVehicle.reg_number}`);
    } catch (err) {
      console.error('Save error:', err.response || err);
      flash(err.response?.data?.message || `Save failed: ${err.message}`, true);
    } finally { setSaving(false); }
  };

  const totalAlerts = Object.values(stats).reduce((sum, s) => sum + (s.expired||0) + (s.critical||0), 0);

  return (
    <div>
      <PageHeader
        title="Renewals & Document Tracker"
        sub={totalAlerts > 0 ? `⚠ ${totalAlerts} urgent items need attention` : 'All documents tracked'}
      />

      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      {/* ── Current / History toggle ── */}
      <div className="flex gap-2 mb-4">
        {[['current','📌 Current Status'],['history','🕘 Renewal History'],
          ['lease', `🏦 Lease Expiry${leaseSoon + leaseOver > 0 ? ` (${leaseSoon + leaseOver})` : ''}`],
          ['licence', `🪪 Driver Licences${licSoon + licOver > 0 ? ` (${licSoon + licOver})` : ''}`],
          ['warranty', `🔋 Battery / Tyre Warranty${wSoon + wOver > 0 ? ` (${wSoon + wOver})` : ''}`]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${view === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {view === 'warranty' ? (
        <div className="space-y-4">
          {(wSoon > 0 || wOver > 0) && (
            <div className="flex gap-3 flex-wrap">
              {wOver > 0 && (
                <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  ⚠ {wOver} warrant{wOver === 1 ? 'y' : 'ies'} expired
                </div>
              )}
              {wSoon > 0 && (
                <div className="px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
                  ⏳ {wSoon} warrant{wSoon === 1 ? 'y' : 'ies'} expiring within 60 days
                </div>
              )}
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>{['Vehicle','Part','Brand / Size','Serial No.','Qty','Installed','Warranty','Ends','Remaining'].map(h =>
                  <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {warranties.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                    No batteries or tyres registered yet — details are captured when the invoice is entered
                  </td></tr>
                )}
                {warranties.map(w => {
                  const over = w.days_left < 0;
                  const cls = over ? 'bg-red-100 text-red-700'
                    : w.days_left <= 30 ? 'bg-amber-100 text-amber-800'
                    : w.days_left <= 60 ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-700';
                  return (
                    <tr key={w.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <span className="font-mono text-primary-600 font-semibold text-xs">{w.reg_number}</span>
                        <p className="text-xs text-gray-400 capitalize">{w.vehicle_type}</p>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 capitalize">{w.part_type}</span>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {w.brand || '—'}
                        <p className="text-gray-400">{w.size}{w.capacity_ah ? ` · ${w.capacity_ah}Ah` : ''}</p>
                      </td>
                      <td className="px-3 py-3 text-xs font-mono">{w.serial_no || '—'}</td>
                      <td className="px-3 py-3 text-xs">{w.quantity}</td>
                      <td className="px-3 py-3 text-xs">{w.install_date || '—'}</td>
                      <td className="px-3 py-3 text-xs">
                        {w.warranty_months ? `${w.warranty_months} mo` : '—'}
                        {w.prorata_months ? <p className="text-gray-400">pro-rata {w.prorata_months} mo</p> : null}
                      </td>
                      <td className="px-3 py-3 text-xs">{w.warranty_end}</td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
                          {over ? `expired ${Math.abs(w.days_left)}d ago`
                                : w.months_left >= 1 ? `${w.months_left} mo left`
                                : `${w.days_left} days left`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === 'licence' ? (
        <div className="space-y-4">
          {(licSoon > 0 || licOver > 0) && (
            <div className="flex gap-3 flex-wrap">
              {licOver > 0 && (
                <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  ⚠ {licOver} licence{licOver === 1 ? '' : 's'} expired
                </div>
              )}
              {licSoon > 0 && (
                <div className="px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
                  ⏳ {licSoon} licence{licSoon === 1 ? '' : 's'} expiring within 60 days
                </div>
              )}
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>{['Driver','Contact','Licence Type','Licence No.','Expiry','Time Remaining','Driver Status'].map(h =>
                  <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {licences.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    No licence expiry dates recorded yet
                  </td></tr>
                )}
                {licences.map(l => {
                  const over = l.days_left < 0;
                  const cls = over ? 'bg-red-100 text-red-700'
                    : l.days_left <= 30 ? 'bg-amber-100 text-amber-800'
                    : l.days_left <= 60 ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-700';
                  return (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-xs font-medium">{l.driver_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-500">{l.phone || '—'}</td>
                      <td className="px-3 py-3 text-xs">
                        <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">{l.license_type}</span>
                      </td>
                      <td className="px-3 py-3 text-xs font-mono">{l.license_no || '—'}</td>
                      <td className="px-3 py-3 text-xs">{l.expiry_date}</td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
                          {over ? `expired ${Math.abs(l.days_left)} days ago`
                                : l.months_left >= 1 ? `${l.months_left} month${l.months_left === 1 ? '' : 's'} left`
                                : `${l.days_left} days left`}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs capitalize">
                        <span className={l.driver_status === 'active' ? 'text-green-700' : 'text-gray-400'}>
                          {l.driver_status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === 'lease' ? (
        <div className="space-y-4">
          {(leaseSoon > 0 || leaseOver > 0) && (
            <div className="flex gap-3 flex-wrap">
              {leaseOver > 0 && (
                <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  ⚠ {leaseOver} lease{leaseOver === 1 ? '' : 's'} already ended
                </div>
              )}
              {leaseSoon > 0 && (
                <div className="px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
                  ⏳ {leaseSoon} lease{leaseSoon === 1 ? '' : 's'} ending within 3 months
                </div>
              )}
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>{['Vehicle','Owner Company','Lease Provider','Amount','Period','Time Remaining','Lease Status'].map(h =>
                  <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {leases.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    No leased vehicles — tick “This vehicle is on lease” on a vehicle and set its lease end date
                  </td></tr>
                )}
                {leases.map(l => {
                  const m = l.months_left;
                  const over = l.days_left < 0;
                  const cls = over ? 'bg-red-100 text-red-700'
                    : m <= 3  ? 'bg-amber-100 text-amber-800'
                    : m <= 6  ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-700';
                  return (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <span className="font-mono text-primary-600 font-semibold text-xs">{l.reg_number}</span>
                        <p className="text-xs text-gray-400">{l.brand} {l.model}</p>
                      </td>
                      <td className="px-3 py-3 text-xs">{l.company_name || '—'}</td>
                      <td className="px-3 py-3 text-xs">{l.lease_provider || '—'}</td>
                      <td className="px-3 py-3 text-xs">{l.lease_amount ? formatRs(l.lease_amount) : '—'}</td>
                      <td className="px-3 py-3 text-xs">
                        {l.lease_start_date || '—'} → {l.lease_end_date}
                        {l.lease_duration && <p className="text-gray-400">{l.lease_duration}</p>}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
                          {over ? `ended ${Math.abs(l.days_left)} days ago`
                                : m >= 1 ? `${m} month${m === 1 ? '' : 's'} left`
                                : `${l.days_left} days left`}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs capitalize">{l.lease_status || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === 'history' ? (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vehicle</label>
              <select value={histVehicle} onChange={e => setHistVehicle(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">All vehicles</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Document</label>
              <select value={histType} onChange={e => setHistType(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">All documents</option>
                {DOC_TYPES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
            <p className="text-xs text-gray-400 ml-auto">
              Total paid: <strong>{formatRs(history.reduce((t, h) => t + Number(h.amount || 0), 0))}</strong> over {history.length} renewal(s)
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>{['Vehicle','Document','No. / Provider','Active Period','Sum Insured','Premium / Amount','Payment','Status','Recorded'].map(h =>
                  <th key={h} className="px-3 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {history.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No renewal history yet — records appear here as you renew documents</td></tr>}
                {history.map(h => {
                  const dt = DOC_TYPES.find(d => d.key === h.doc_type);
                  return (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 font-mono text-primary-600 font-semibold text-xs">{h.vehicle?.reg_number}
                        <p className="text-gray-400 font-sans font-normal">{h.vehicle?.brand} {h.vehicle?.model}</p></td>
                      <td className="px-3 py-3 text-xs">{dt?.icon} {dt?.label || h.doc_type}</td>
                      <td className="px-3 py-3 text-xs">{h.doc_no || '—'}
                        {h.provider && <p className="text-gray-400">{h.provider}</p>}</td>
                      <td className="px-3 py-3 text-xs">{h.from_date || '—'} → {h.to_date || '—'}</td>
                      <td className="px-3 py-3 text-xs">{h.sum_insured ? formatRs(h.sum_insured) : '—'}</td>
                      <td className="px-3 py-3 text-xs">
                        {h.approved_premium ? <p>{formatRs(h.approved_premium)}</p> : null}
                        {h.premium_quoted ? <p className="text-gray-400">quoted {formatRs(h.premium_quoted)}</p> : null}
                        {h.amount ? <p className="font-medium">{formatRs(h.amount)}</p> : null}
                      </td>
                      <td className="px-3 py-3 text-xs capitalize">
                        {h.payment_method || '—'}{h.cheque_no ? ` · ${h.cheque_no}` : ''}
                        {h.payment_date && <p className="text-gray-400">{h.payment_date}</p>}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${h.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{h.status}</span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-400">{h.creator?.name || '—'}
                        <p>{h.created_at ? new Date(h.created_at).toLocaleDateString('en-GB') : ''}</p></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (<>

      {/* ── Summary cards for all 4 doc types ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {DOC_TYPES.map(dt => {
          const s    = stats[dt.key] || {};
          const c    = COLOR[dt.color];
          const isActive = activeDoc === dt.key;
          return (
            <button key={dt.key} onClick={() => { setActiveDoc(dt.key); setFilterStat('all'); setSearch(''); }}
              className={`text-left rounded-xl border-2 transition-all p-4
                ${isActive ? `${c.border} ${c.light} border-2` : 'bg-white border-gray-200 hover:border-gray-300'}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{dt.icon}</span>
                <span className={`text-xs font-semibold uppercase tracking-wide ${isActive ? c.text : 'text-gray-600'}`}>
                  {dt.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {s.expired  > 0 && <span className="bg-red-100    text-red-700    px-2 py-0.5 rounded font-medium">🔴 {s.expired} expired</span>}
                {s.critical > 0 && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">🟠 {s.critical} critical</span>}
                {s.warning  > 0 && <span className="bg-amber-100  text-amber-700  px-2 py-0.5 rounded font-medium">🟡 {s.warning} warning</span>}
                {s.valid    > 0 && <span className="bg-green-100  text-green-700  px-2 py-0.5 rounded">✓ {s.valid} valid</span>}
                {s.not_set  > 0 && <span className="bg-gray-100   text-gray-500   px-2 py-0.5 rounded">— {s.not_set} not set</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Doc type header ── */}
      <div className={`${col.hdr} rounded-t-xl px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{doc.icon}</span>
          <div>
            <p className="text-white font-bold text-base">{doc.label}</p>
            <p className="text-white/70 text-xs">{filtered.length} vehicles shown</p>
          </div>
        </div>
        {/* Filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {[
            { val:'all',      label:'All' },
            { val:'expired',  label:'Expired' },
            { val:'critical', label:'< 30 days' },
            { val:'warning',  label:'< 60 days' },
            { val:'valid',    label:'Valid' },
            { val:'not_set',  label:'Not Set' },
          ].map(f => (
            <button key={f.val} onClick={() => setFilterStat(f.val)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all
                ${filterStat === f.val ? 'bg-white text-gray-800' : 'bg-white/20 text-white hover:bg-white/30'}`}>
              {f.label}
              {stats[doc.key]?.[f.val] > 0 && f.val !== 'all' && (
                <span className="ml-1 font-bold">({stats[doc.key]?.[f.val]})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Search + table ── */}
      <div className={`${col.light} ${col.border} border border-t-0 rounded-b-xl p-4`}>
        <div className="mb-4">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by reg number, brand, model…"
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-72 outline-none focus:ring-2 focus:ring-primary-300 bg-white" />
        </div>

        {loading ? <Loading /> : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  {['Vehicle','Brand / Model','Document No.','Expiry Date','Days Left','Status','Last Updated','Action'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No vehicles match this filter</td></tr>
                )}
                {filtered.map(v => {
                  const si = statusInfo(v[doc.expiry_field]);
                  const d  = daysLeft(v[doc.expiry_field]);
                  return (
                    <tr key={v.id} className={`hover:bg-gray-50 ${si.label === 'Expired' ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-primary-600">{v.reg_number}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{v.brand} {v.model}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {v[doc.no_field] || <span className="text-gray-400 italic">Not recorded</span>}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {v[doc.expiry_field] || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {d === null ? (
                          <span className="text-gray-400 text-xs">—</span>
                        ) : (
                          <span className={`font-bold text-sm ${d < 0 ? 'text-red-600' : d <= 30 ? 'text-orange-600' : d <= 60 ? 'text-amber-600' : 'text-green-600'}`}>
                            {si.days}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${si.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${si.dot}`}></span>
                          {si.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {can('admin','transport_officer') && (
                          <Button size="sm"
                            variant={si.label === 'Expired' || si.label === 'Critical' ? 'primary' : 'ghost'}
                            onClick={() => openRenew(v, doc)}>
                            {si.label === 'Not Set' ? 'Add' : 'Renew'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>)}

      {/* ── Renew Modal ── */}
      <Modal open={renewModal} onClose={() => setRenewModal(false)}
        title={`${renewDoc?.label} Renewal — ${renewVehicle?.reg_number}`} size="lg">
        {renewVehicle && renewDoc && (
          <form onSubmit={saveRenewal} className="space-y-4">

            {/* Current info */}
            <div className={`${COLOR[renewDoc.color].light} ${COLOR[renewDoc.color].border} border rounded-xl px-4 py-3`}>
              <p className="text-xs font-semibold text-gray-500 mb-2">Current Record</p>
              <div className="flex justify-between text-sm">
                <div>
                  <p className="text-xs text-gray-400">Document No.</p>
                  <p className="font-medium">{renewVehicle[renewDoc.no_field] || '—'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Expiry</p>
                  <p className="font-medium">
                    {renewVehicle[renewDoc.expiry_field]
                      ? <>{renewVehicle[renewDoc.expiry_field]}
                          <span className={`ml-2 text-xs font-bold
                            ${daysLeft(renewVehicle[renewDoc.expiry_field]) < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                            ({daysLeft(renewVehicle[renewDoc.expiry_field]) < 0
                              ? `Expired ${Math.abs(daysLeft(renewVehicle[renewDoc.expiry_field]))}d ago`
                              : `${daysLeft(renewVehicle[renewDoc.expiry_field])}d left`})
                          </span>
                        </>
                      : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* New values */}
            {/* Vehicle details (auto from selection) */}
            <div className="bg-gray-50 border rounded-xl p-3 grid grid-cols-3 gap-2 text-xs">
              {[['Vehicle No.', renewVehicle.reg_number], ['Make', renewVehicle.brand], ['Model', renewVehicle.model],
                ['Engine No.', renewVehicle.engine_number], ['Chassis No.', renewVehicle.chassis_number],
                ['Fuel Type', renewVehicle.fuel_type]].map(([k, v]) => (
                <div key={k}><span className="text-gray-400">{k}:</span> <span className="font-medium capitalize">{v || '—'}</span></div>
              ))}
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New / Updated Details</p>
              <div className="grid grid-cols-2 gap-3">
                <Input label={`${renewDoc.label} / Policy No.`} value={renewForm.doc_no}
                  onChange={e => setRenewForm({ ...renewForm, doc_no: e.target.value })}
                  placeholder={`e.g. ${renewDoc.key === 'insurance' ? 'INS-2026-001258' : renewDoc.key === 'revenue_licence' ? 'RL-2026-004876' : renewDoc.key === 'emission_test' ? 'EM-2026-002235' : 'FC-2026-003312'}`} />
                <Input label={renewDoc.key === 'insurance' ? 'Insurance Provider' : 'Issued By'} value={renewForm.provider}
                  onChange={e => setRenewForm({ ...renewForm, provider: e.target.value })}
                  placeholder={renewDoc.key === 'insurance' ? 'e.g. SLIC, Ceylinco' : 'e.g. DMT'} />
                <Input label="Active Period — From" type="date" value={renewForm.from_date}
                  onChange={e => setRenewForm({ ...renewForm, from_date: e.target.value })} />
                <Input label="To (Expiry) *" type="date" required value={renewForm.expiry}
                  onChange={e => setRenewForm({ ...renewForm, expiry: e.target.value })} />
              </div>

              {renewDoc.key === 'insurance' && (
                <div className="grid grid-cols-3 gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <Input label="Sum Insured (Rs.)" type="number" step="0.01" value={renewForm.sum_insured}
                    onChange={e => setRenewForm({ ...renewForm, sum_insured: e.target.value })} />
                  <Input label="Premium Quoted (Rs.)" type="number" step="0.01" value={renewForm.premium_quoted}
                    onChange={e => setRenewForm({ ...renewForm, premium_quoted: e.target.value })} />
                  <Input label="Approved Premium (Rs.)" type="number" step="0.01" value={renewForm.approved_premium}
                    onChange={e => setRenewForm({ ...renewForm, approved_premium: e.target.value })} />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                <Input label={renewDoc.key === 'insurance' ? 'Premium Paid (Rs.)' : 'Amount Paid (Rs.)'}
                  type="number" step="0.01" value={renewForm.amount}
                  onChange={e => setRenewForm({ ...renewForm, amount: e.target.value })} />
                <Select label="Cheque / Cash" value={renewForm.payment_method}
                  onChange={e => setRenewForm({ ...renewForm, payment_method: e.target.value })}>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                </Select>
                {renewForm.payment_method === 'cheque' && (
                  <Input label="Cheque No." value={renewForm.cheque_no}
                    onChange={e => setRenewForm({ ...renewForm, cheque_no: e.target.value })} />
                )}
                <Input label="Payment Date" type="date" value={renewForm.payment_date}
                  onChange={e => setRenewForm({ ...renewForm, payment_date: e.target.value })} />
              </div>
            </div>

            {/* Preview new status */}
            {renewForm.expiry && (() => {
              const si = statusInfo(renewForm.expiry);
              return (
                <div className={`${si.cls} border rounded-lg px-3 py-2 flex items-center gap-2 text-sm`}>
                  <span className={`w-2 h-2 rounded-full ${si.dot}`}></span>
                  New status: <strong>{si.label}</strong>
                  {si.days && <span className="ml-1 text-xs">({si.days})</span>}
                </div>
              );
            })()}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setRenewModal(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Renewal'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
